import { createStyles } from '@mantine/core';
import { IconFlame } from '@tabler/icons-react';
import { useLayoutEffect, useRef } from 'react';

import { HEADER_HEIGHT } from '~/domains/common/components/AppShellHeader2/constants';
import type { PropsFiltersType } from '~/domains/contest/domains/services/interfaces/propsService/api';
import { UnstyledButton } from '~/domains/design-system/Button';
import { ContainerNested } from '~/domains/design-system/Container';
import { textStyles } from '~/domains/design-system/Text';
import {
  QUICK_PICKS_TABS_HEIGHT,
  QUICK_PICKS_TABS_HEIGHT_WITH_SUB_NAV,
} from '../../QuickPickLayout/QuickPickLayout';

const useStyles = createStyles((theme, { isBelowSubNav }: { isBelowSubNav?: boolean }) => ({
  container: {
    display: 'flex',
    overflowX: 'auto',
    position: 'sticky',
    top: isBelowSubNav ? HEADER_HEIGHT + QUICK_PICKS_TABS_HEIGHT : HEADER_HEIGHT,
    zIndex: 89,
    backgroundColor: theme.colors.gray2[0],
  },
  item: {
    ...textStyles['headline-small'],
    padding: `${theme.other.spacing._8}px ${theme.other.spacing._16}px`,
    borderBottom: '2px solid transparent',
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    gap: theme.other.spacing._4,

    '&.isActive': {
      borderBottomColor: theme.colors.blue[9],
    },

    '@media (hover: hover)': {
      transition: 'opacity 150ms ease-in-out',

      '&:hover': {
        opacity: 0.5,
      },
    },
  },
}));

function Item({
  option,
  isActive,
  onChange,
  isBelowSubNav,
}: {
  option: PropsFiltersType;
  isActive?: boolean;
  onChange: (newValue: string) => void;
  isBelowSubNav?: boolean;
}) {
  const { classes, cx } = useStyles({ isBelowSubNav });
  const elRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    if (!elRef.current) {
      return;
    }
    if (!isActive) {
      return;
    }

    window.scrollTo({
      top: QUICK_PICKS_TABS_HEIGHT_WITH_SUB_NAV,
      behavior: 'smooth',
    });
  }, [isActive]);

  return (
    <UnstyledButton
      ref={elRef}
      className={cx(classes.item, { isActive })}
      onClick={() => {
        if (isActive) {
          return;
        }
        onChange(option.id);
      }}
    >
      {option.display_name}
      {option.id === 'popular' && <IconFlame />}
    </UnstyledButton>
  );
}

function FilterType({
  options,
  value,
  onChange,
  isBelowSubNav,
}: {
  options: PropsFiltersType[];
  value: string;
  onChange: (newValue: string) => void;
  isBelowSubNav?: boolean;
}) {
  const { classes } = useStyles({ isBelowSubNav });

  return (
    <ContainerNested className={classes.container}>
      {options.map((option) => (
        <Item key={option.id} option={option} isActive={value === option.id} onChange={onChange} />
      ))}
    </ContainerNested>
  );
}

export default FilterType;
